import { useRef, useState, useEffect, type DragEvent } from "react";
import type { Member, MemberDocument, DocumentType } from "../../../../services/types";
import { DOCUMENT_TYPES, DOC_TYPE_LABEL } from "../../../../config/members";
import { memberService } from "../../../../services";
import { useToast } from "../../../../context/ToastContext";
import { useConfirm } from "../../../../context/ConfirmContext";
import { Card } from "../../../../components/base/Card";
import { Button } from "../../../../components/base/Button";
import { Select } from "../../../../components/base/Select";
import { EmptyState } from "../../../../components/base/EmptyState";
import { Alert } from "../../../../components/base/Alert";
import { Modal } from "../../../../components/base/Modal";
import { formatDate } from "../../../../utils/format";

const MAX_MB = 5;

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,application/pdf";

function DocIcon({ type }: { type: string }) {
  if (type.startsWith("image/")) return <i className="ri-image-2-line text-3xl text-primary-500" />;
  return <i className="ri-file-pdf-2-line text-3xl text-danger-500" />;
}

interface DocumentsTabProps {
  member: Member;
  onUpdate: (member: Member) => void;
  isViewer: boolean;
}

export function DocumentsTab({ member, onUpdate, isViewer }: DocumentsTabProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<DocumentType>("aadhaar");
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Preview and download states
  const [previewDoc, setPreviewDoc] = useState<MemberDocument | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  const handleOpenPreview = async (doc: MemberDocument) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    setPreviewError(null);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    try {
      const blob = await memberService.getDocumentBlob(doc.id);
      const url = URL.createObjectURL(blob);
      setPreviewBlobUrl(url);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Failed to load document preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewDoc(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const handleDownload = async (doc: MemberDocument) => {
    setDownloadingId(doc.id);
    try {
      await memberService.downloadDocument(doc.id, doc.fileName);
      toast.success("Download complete", doc.fileName);
    } catch (err) {
      toast.error("Download failed", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const ingest = async (files: FileList | File[]) => {
    setError(null);
    const list = Array.from(files);
    if (list.length === 0) return;
    const tooBig = list.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) {
      setError(`"${tooBig.name}" exceeds the ${MAX_MB} MB file size limit.`);
      return;
    }
    setSaving(true);
    try {
      let updated = member;
      for (const file of list) {
        updated = await memberService.addDocument(member.id, file, docType);
      }
      onUpdate(updated);
      toast.success("Documents uploaded", `${list.length} document${list.length > 1 ? "s" : ""} added.`);
    } catch (err) {
      toast.error("Upload failed", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (isViewer || saving) return;
    ingest(e.dataTransfer.files);
  };

  const handleDelete = async (doc: MemberDocument) => {
    const ok = await confirm({
      title: "Delete document?",
      message: `${doc.fileName} will be removed from this member's records.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      const updated = await memberService.removeDocument(member.id, doc.id);
      onUpdate(updated);
      toast.success("Document deleted", doc.fileName);
    } catch (err) {
      toast.error("Could not delete", err instanceof Error ? err.message : "Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      {isViewer ? null : (
        <Card title="Upload documents" subtitle={`Accepts images and PDFs, up to ${MAX_MB} MB each.`}>
          <div className="space-y-3">
            <div className="max-w-xs">
              <Select
                label="Document type"
                value={docType}
                onChange={(v) => setDocType(v as DocumentType)}
                options={DOCUMENT_TYPES.map((d) => ({ value: d.value, label: d.label }))}
              />
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => !saving && inputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-4 py-8 text-center transition-colors hover:bg-background-100/40"
            >
              <i className="ri-upload-cloud-2-line text-3xl text-foreground-400" />
              <p className="text-sm text-foreground-700">
                Drag &amp; drop files here, or <span className="font-medium text-primary-600">browse</span>
              </p>
              <p className="text-xs text-foreground-500">Images (PNG, JPG, WEBP) or PDF · Max {MAX_MB} MB each</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) ingest(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            {error ? <Alert variant="error">{error}</Alert> : null}
          </div>
        </Card>
      )}

      <Card title="Uploaded documents" subtitle={`${member.documents.length} file${member.documents.length === 1 ? "" : "s"} on record`}>
        {member.documents.length === 0 ? (
          <EmptyState
            icon="ri-file-list-3-line"
            title="No documents uploaded"
            description="KYC documents uploaded for this member will appear here."
            compact
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {member.documents.map((doc) => (
              <div key={doc.id} className="group relative flex items-start gap-3 rounded-md border border-background-200 p-3 transition-colors hover:border-background-300 hover:bg-background-50/50">
                <div
                  onClick={() => handleOpenPreview(doc)}
                  className="flex h-12 w-12 flex-none cursor-pointer items-center justify-center rounded-md bg-background-100 transition-transform group-hover:scale-105"
                  title="Click to preview"
                >
                  <DocIcon type={doc.mimeType} />
                </div>
                <div className="min-w-0 flex-1">
                  <span
                    onClick={() => handleOpenPreview(doc)}
                    className="block cursor-pointer truncate text-sm font-medium text-foreground-900 hover:text-primary-600"
                    title={doc.fileName}
                  >
                    {doc.fileName}
                  </span>
                  <span className="block text-xs text-foreground-500">{DOC_TYPE_LABEL[doc.docType]}</span>
                  <span className="tnum block text-xs text-foreground-400">{formatSize(doc.size)} · {formatDate(doc.uploadedOn)}</span>
                  <div className="mt-2 flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      icon={<i className="ri-eye-line" />}
                      onClick={() => handleOpenPreview(doc)}
                      title="Preview document"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      icon={downloadingId === doc.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-2-line" />}
                      disabled={downloadingId === doc.id}
                      onClick={() => handleDownload(doc)}
                      title="Download document"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      className="text-danger-600 hover:text-danger-700"
                      icon={<i className="ri-delete-bin-line" />}
                      disabled={isViewer}
                      onClick={() => handleDelete(doc)}
                      title="Delete document"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Document Preview Modal */}
      <Modal
        open={Boolean(previewDoc)}
        onClose={handleClosePreview}
        size="lg"
        title={
          <div className="flex items-center gap-2">
            <span className="max-w-md truncate text-base font-semibold text-foreground-900">
              {previewDoc?.fileName}
            </span>
            {previewDoc ? (
              <span className="rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                {DOC_TYPE_LABEL[previewDoc.docType]}
              </span>
            ) : null}
          </div>
        }
        description={
          previewDoc ? `${formatSize(previewDoc.size)} · Uploaded on ${formatDate(previewDoc.uploadedOn)}` : undefined
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <div>
              {previewBlobUrl && previewDoc ? (
                <Button
                  size="sm"
                  variant="outline"
                  icon={<i className="ri-external-link-line" />}
                  onClick={() => window.open(previewBlobUrl, "_blank")}
                >
                  Open in new tab
                </Button>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {previewDoc ? (
                <Button
                  size="sm"
                  icon={downloadingId === previewDoc.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-2-line" />}
                  disabled={downloadingId === previewDoc.id}
                  onClick={() => handleDownload(previewDoc)}
                >
                  Download
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={handleClosePreview}>
                Close
              </Button>
            </div>
          </div>
        }
      >
        <div className="min-h-[300px]">
          {previewLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <i className="ri-loader-4-line animate-spin text-4xl text-primary-600" />
              <p className="text-sm font-medium text-foreground-600">Loading document preview…</p>
            </div>
          ) : previewError ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <i className="ri-error-warning-line text-2xl" />
              </div>
              <p className="text-sm font-semibold text-foreground-900">{previewError}</p>
              <p className="text-xs text-foreground-500">You can still download the file directly to view it.</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => previewDoc && handleOpenPreview(previewDoc)}>
                  Retry
                </Button>
                {previewDoc ? (
                  <Button size="sm" icon={<i className="ri-download-2-line" />} onClick={() => handleDownload(previewDoc)}>
                    Download File
                  </Button>
                ) : null}
              </div>
            </div>
          ) : previewBlobUrl && previewDoc ? (
            <div className="flex flex-col items-center justify-center">
              {previewDoc.mimeType.startsWith("image/") ? (
                <div className="relative flex max-h-[70vh] w-full items-center justify-center overflow-auto rounded-lg bg-background-100/50 p-2">
                  <img
                    src={previewBlobUrl}
                    alt={previewDoc.fileName}
                    className="max-h-[66vh] max-w-full rounded object-contain shadow-sm"
                  />
                </div>
              ) : previewDoc.mimeType === "application/pdf" ? (
                <iframe
                  src={previewBlobUrl}
                  title={previewDoc.fileName}
                  className="h-[70vh] w-full rounded-lg border border-background-200 bg-background-50"
                />
              ) : (
                <div className="space-y-3 py-16 text-center">
                  <DocIcon type={previewDoc.mimeType} />
                  <p className="text-sm font-medium text-foreground-800">
                    Inline preview is not available for this file type.
                  </p>
                  <Button icon={<i className="ri-download-2-line" />} onClick={() => handleDownload(previewDoc)}>
                    Download {previewDoc.fileName}
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}